import type { Rect } from '../render/geometry';

/** Uniform-grid spatial index over page units. Items are registered by bounding box. */
export class Grid<T> {
  private cells = new Map<number, Set<T>>();
  private boxes = new Map<T, Rect>();

  constructor(private cell = 100) {}

  private key(cx: number, cy: number): number {
    // Pages are small; offset so negative coordinates (unclipped strokes) still pack.
    return (cx + 1000) * 100_000 + (cy + 1000);
  }

  private range(r: Rect): [number, number, number, number] {
    const c = this.cell;
    return [Math.floor(r.minX / c), Math.floor(r.minY / c), Math.floor(r.maxX / c), Math.floor(r.maxY / c)];
  }

  insert(item: T, box: Rect): void {
    this.remove(item);
    this.boxes.set(item, box);
    const [x0, y0, x1, y1] = this.range(box);
    for (let cx = x0; cx <= x1; cx++) {
      for (let cy = y0; cy <= y1; cy++) {
        const k = this.key(cx, cy);
        let s = this.cells.get(k);
        if (!s) this.cells.set(k, (s = new Set()));
        s.add(item);
      }
    }
  }

  remove(item: T): void {
    const box = this.boxes.get(item);
    if (!box) return;
    this.boxes.delete(item);
    const [x0, y0, x1, y1] = this.range(box);
    for (let cx = x0; cx <= x1; cx++) {
      for (let cy = y0; cy <= y1; cy++) {
        const k = this.key(cx, cy);
        const s = this.cells.get(k);
        s?.delete(item);
        if (s?.size === 0) this.cells.delete(k);
      }
    }
  }

  /** Items whose bounding box intersects `r`. */
  query(r: Rect): Set<T> {
    const out = new Set<T>();
    const [x0, y0, x1, y1] = this.range(r);
    for (let cx = x0; cx <= x1; cx++) {
      for (let cy = y0; cy <= y1; cy++) {
        const s = this.cells.get(this.key(cx, cy));
        if (!s) continue;
        for (const it of s) {
          const b = this.boxes.get(it)!;
          if (b.maxX >= r.minX && b.minX <= r.maxX && b.maxY >= r.minY && b.minY <= r.maxY) out.add(it);
        }
      }
    }
    return out;
  }

  get size(): number {
    return this.boxes.size;
  }
}
